import { BuildingLayoutModel, LayoutModel, LevelLayoutModel, ModelCategory } from '@paragon/contract-library';
import {
  getInsertionsWithPendingChangesGroupedByParentGuid,
  getLayoutModelsWithPendingChanges,
  layoutModelCreated,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { notNullish, ReadonlyRecord } from '@paragon/utilities';

import { RootState } from '../../index';
import {
  getAllBuildings,
  getAllBuildingsDictionary,
  getAllLevelsDictionary,
} from '../../selectors/layoutDataSelectors';
import { DEFAULT_BEARING_ENVELOPE_HEIGHT } from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { saveEntireLayout } from '../editorActions';
import { selectItemByGuid } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionReturn } from '../thunkActions';

type HierarchyItemCreationOptions = { selectAfterCreation?: boolean };

const createLevelModel =
  (buildingGuid: string, options?: HierarchyItemCreationOptions): ThunkActionReturn<{ levelGuid: string }> =>
  (dispatch, getState) => {
    const context = getState().application.openContext;
    if (context?.type !== 'project') throw new Error("Can't create a level outside of a project.");
    const { projectGuid } = context;

    const otherLevelsInBuilding = getSiblingsOfSameType(getState(), getAllLevelsDictionary(getState()), buildingGuid);

    const name = getNameForNewModel(otherLevelsInBuilding);
    const materialSelection = getMaterialSelectionForModel(getState(), buildingGuid);
    const index =
      otherLevelsInBuilding.length > 0 ? Math.min(...otherLevelsInBuilding.map((level) => level.index ?? 0)) - 1 : 0;

    const levelModel: LevelLayoutModel = {
      type: 'level',
      modelType: 'Level',
      category: ModelCategory.IntermediateModel,

      guid: crypto.randomUUID(),
      index,
      name,
      materialSelection,
      plateElevation: DEFAULT_BEARING_ENVELOPE_HEIGHT,
      wallHeight: DEFAULT_BEARING_ENVELOPE_HEIGHT,
      floorHeight: 18,
    };

    dispatch(layoutModelCreated({ projectGuid, parentGuid: buildingGuid, model: levelModel }));

    if (options?.selectAfterCreation) {
      dispatch(selectItemByGuid(levelModel.guid, 'level'));
    }

    return { levelGuid: levelModel.guid };
  };

const createBuildingModel =
  (options?: HierarchyItemCreationOptions): ThunkActionReturn<{ buildingGuid: string; levelGuid: string }> =>
  (dispatch, getState) => {
    const context = getState().application.openContext;
    if (context?.type !== 'project') throw new Error("Can't create a building outside of a project.");
    const { projectGuid } = context;

    const otherBuildings = getSiblingsOfSameType(getState(), getAllBuildingsDictionary(getState()), projectGuid);
    const name = getNameForNewModel(otherBuildings);
    const materialSelection = getMaterialSelectionForModel(getState(), projectGuid);

    const buildingModel: BuildingLayoutModel = {
      type: 'building',
      modelType: 'Building',
      category: ModelCategory.IntermediateModel,

      guid: crypto.randomUUID(),
      name,
      materialSelection,
    };

    dispatch(layoutModelCreated({ projectGuid, parentGuid: projectGuid, model: buildingModel }));

    // do not pass in creation options; if needed you can add a `levelCreationOptions` parameter to this action
    const { levelGuid } = dispatch(createLevelModel(buildingModel.guid));

    if (options?.selectAfterCreation) {
      dispatch(selectItemByGuid(buildingModel.guid, 'building'));
    }

    return { buildingGuid: buildingModel.guid, levelGuid };
  };

function getSiblingsOfSameType<T extends LayoutModel>(
  state: RootState,
  modelsOfType: ReadonlyRecord<string, T>,
  parentGuid: string,
) {
  return (
    getInsertionsWithPendingChangesGroupedByParentGuid(state)
      [parentGuid]?.map((insertion) => modelsOfType[insertion.objectGuid])
      .filter(notNullish) ?? []
  );
}

function getNameForNewModel(siblingsOfSameType: readonly LayoutModel[]) {
  let modelNumber = siblingsOfSameType.length + 1;
  const numericSiblingNames = siblingsOfSameType.map((sibling) => parseInt(sibling.name)).filter(Number.isFinite);

  if (numericSiblingNames.includes(modelNumber)) {
    modelNumber = Math.max(...numericSiblingNames) + 1;
  }

  return modelNumber.toString();
}

function getMaterialSelectionForModel(state: RootState, modelGuid: string) {
  const materialSelection = getLayoutModelsWithPendingChanges(state)[modelGuid]?.materialSelection;
  if (!materialSelection) {
    throw new Error(`Could not find material selection for model ${modelGuid}.`);
  }
  return materialSelection;
}

export const createBuildingAndSaveProject =
  (options?: HierarchyItemCreationOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { levelGuid } = dispatch(createBuildingModel(options));

    // when the first building is added to a project, we want to automatically assign everything to it
    if (getAllBuildings(getState()).length === 1) {
      dispatch(layoutSlice.actions.universalLevelSet(levelGuid));
    }

    await Promise.all([dispatch(saveAllProjectChanges()), dispatch(saveEntireLayout())]);
  };

export const createLevelAndSaveProject =
  (buildingGuid: string, options?: HierarchyItemCreationOptions): AsyncThunkActionVoid =>
  async (dispatch) => {
    dispatch(createLevelModel(buildingGuid, options));
    await dispatch(saveAllProjectChanges());
  };
